import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import '../components/MessagePopup.css';

const AUTO_DISMISS_MS = 3000;
const MAX_TOASTS = 3;

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef({});
  const location = useLocation();
  const isBattleScreen = location.pathname === '/battle';

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }
  }, []);

  const showToast = useCallback((message, type = 'error') => {
    if (!message) return;
    const id = Date.now() + Math.random();

    setToasts((prev) => {
      const next = [...prev, { id, message, type }];
      if (next.length <= MAX_TOASTS) return next;
      const removed = next.shift();
      if (removed?.id && timeoutsRef.current[removed.id]) {
        clearTimeout(timeoutsRef.current[removed.id]);
        delete timeoutsRef.current[removed.id];
      }
      return next;
    });

    timeoutsRef.current[id] = setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
  }, [dismissToast]);

  useEffect(() => () => {
    Object.values(timeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutsRef.current = {};
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {!isBattleScreen && toasts.length > 0 && (
        <div className="app-toast-container" aria-live="polite">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`app-toast app-toast--${toast.type}`}
              role="status"
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
